/**
 * Sistema di persistenza localStorage per mock API fornitori
 * 
 * Questo modulo gestisce il salvataggio e caricamento dei dati mock
 * nel localStorage del browser, permettendo persistenza tra sessioni.
 */

import { FornitoreDettagliato, FornitoreBase, LookupData, MenuItem } from './types';
import { FORNITORI_DETTAGLIATI, FORNITORI_BASE, LOOKUP_DATA, MENU_ITEMS } from './fornitori-data';

// Chiavi per localStorage
const STORAGE_KEYS = {
  FORNITORI_DETTAGLIATI: 'mock_fornitori_dettagliati',
  FORNITORI_BASE: 'mock_fornitori_base',
  LOOKUP_DATA: 'mock_lookup_data',
  MENU_ITEMS: 'mock_menu_items',
  NEXT_ID: 'mock_next_fornitore_id',
  METADATA: 'mock_metadata'
} as const;

interface StorageMetadata {
  lastUpdate: string;
  version: string;
  totalFornitori: number;
}

/**
 * Classe per gestire la persistenza dei dati mock
 */
export class MockPersistence {
  private static isEnabled = true;

  /**
   * Abilita/disabilita la persistenza
   */
  static setEnabled(enabled: boolean): void {
    this.isEnabled = enabled;
    if (!enabled) {
      this.clearAll();
    }
  }

  /**
   * Verifica se la persistenza è abilitata
   */
  static isStorageEnabled(): boolean {
    return this.isEnabled && typeof localStorage !== 'undefined';
  }

  /**
   * Salva i dati nel localStorage
   */
  private static saveToStorage<T>(key: string, data: T): void {
    if (!this.isStorageEnabled()) return;
    
    try {
      localStorage.setItem(key, JSON.stringify(data));
    } catch (error) {
      console.warn('[MOCK PERSISTENCE] Errore salvamento localStorage:', error);
    }
  }

  /**
   * Carica i dati dal localStorage
   */
  private static loadFromStorage<T>(key: string, defaultValue: T): T {
    if (!this.isStorageEnabled()) return defaultValue;
    
    try {
      const stored = localStorage.getItem(key);
      return stored ? JSON.parse(stored) : defaultValue;
    } catch (error) {
      console.warn('[MOCK PERSISTENCE] Errore caricamento localStorage:', error);
      return defaultValue;
    }
  }

  /**
   * Inizializza il storage con i dati di default se non esistono
   */
  static initializeStorage(): void {
    if (!this.isStorageEnabled()) return;

    // Carica o inizializza i dati
    const existing = this.loadFromStorage<StorageMetadata | null>(STORAGE_KEYS.METADATA, null);
    
    if (!existing) {
      console.log('[MOCK PERSISTENCE] Inizializzazione storage con dati di default');
      this.saveDefaultData();
    } else {
      console.log('[MOCK PERSISTENCE] Storage esistente trovato, versione:', existing.version);
    }
  }

  /**
   * Salva i dati di default nel storage
   */
  private static saveDefaultData(): void {
    this.saveToStorage(STORAGE_KEYS.FORNITORI_DETTAGLIATI, FORNITORI_DETTAGLIATI);
    this.saveToStorage(STORAGE_KEYS.FORNITORI_BASE, FORNITORI_BASE);
    this.saveToStorage(STORAGE_KEYS.LOOKUP_DATA, LOOKUP_DATA);
    this.saveToStorage(STORAGE_KEYS.MENU_ITEMS, MENU_ITEMS);
    this.saveToStorage(STORAGE_KEYS.NEXT_ID, FORNITORI_BASE.length + 1);
    
    this.updateMetadata();
  }

  /**
   * Aggiorna i metadati del storage
   */
  private static updateMetadata(): void {
    const metadata: StorageMetadata = {
      lastUpdate: new Date().toISOString(),
      version: '1.0.0',
      totalFornitori: this.getAllFornitori().length
    };
    
    this.saveToStorage(STORAGE_KEYS.METADATA, metadata);
  }

  /**
   * Ottiene tutti i fornitori dal storage
   */
  static getAllFornitori(): FornitoreBase[] {
    return this.loadFromStorage(STORAGE_KEYS.FORNITORI_BASE, FORNITORI_BASE);
  }

  /**
   * Ottiene tutti i fornitori dettagliati dal storage
   */
  static getAllFornitoriDettagliati(): FornitoreDettagliato[] {
    return this.loadFromStorage(STORAGE_KEYS.FORNITORI_DETTAGLIATI, FORNITORI_DETTAGLIATI);
  }

  /**
   * Ottiene un fornitore per ID
   */
  static getFornitoreById(id: number): FornitoreBase | null {
    const fornitori = this.getAllFornitori();
    return fornitori.find(f => f.id === id) || null;
  }

  /**
   * Ottiene un fornitore dettagliato per ID
   */
  static getFornitoreDetagliatoById(id: number): FornitoreDettagliato | null {
    const fornitori = this.getAllFornitoriDettagliati();
    return fornitori.find(f => f.id === id) || null;
  }

  /**
   * Aggiunge un nuovo fornitore
   */
  static addFornitore(fornitoreDettagliato: FornitoreDettagliato): { id: number; success: boolean } {
    if (!this.isStorageEnabled()) {
      return { id: 0, success: false };
    }

    try {
      const nextId = this.loadFromStorage(STORAGE_KEYS.NEXT_ID, FORNITORI_BASE.length + 1);
      
      // Assegna l'ID al fornitore dettagliato
      fornitoreDettagliato.id = nextId;
      
      // Aggiungi ai fornitori dettagliati
      const dettagliati = this.getAllFornitoriDettagliati();
      dettagliati.push(fornitoreDettagliato);
      this.saveToStorage(STORAGE_KEYS.FORNITORI_DETTAGLIATI, dettagliati);

      // Crea versione base per la lista
      const fornitoreBase: FornitoreBase = {
        id: nextId,
        ragioneSociale: fornitoreDettagliato.azienda.ragione_sociale,
        partitaIva: fornitoreDettagliato.operatore_economico.partita_iva,
        codiceFiscale: fornitoreDettagliato.operatore_economico.codice_fiscale_ditta,
        email: fornitoreDettagliato.azienda.email_pec,
        telefono: fornitoreDettagliato.azienda.telefono || undefined,
        cellulare: fornitoreDettagliato.referente_legale.telefono_1 || undefined,
        indirizzo: fornitoreDettagliato.azienda.indirizzo,
        cap: fornitoreDettagliato.azienda.cap,
        citta: fornitoreDettagliato.azienda.comune,
        provincia: fornitoreDettagliato.azienda.provincia,
        stato: fornitoreDettagliato.azienda.stato_attivita === "Attiva" ? "ATTIVO" : 
               fornitoreDettagliato.azienda.stato_attivita === "Sospesa temporaneamente" ? "SOSPESO" : "ELIMINATO",
        tipologia: "AZIENDA",
        dataCreazione: new Date().toISOString(),
        dataUltimaModifica: new Date().toISOString(),
        pec: fornitoreDettagliato.azienda.email_pec,
        sdi: fornitoreDettagliato.pagamenti.sdi || undefined,
        iban: fornitoreDettagliato.pagamenti.iban || undefined,
        note: fornitoreDettagliato.pagamenti.note || undefined
      };

      // Aggiungi ai fornitori base
      const base = this.getAllFornitori();
      base.push(fornitoreBase);
      this.saveToStorage(STORAGE_KEYS.FORNITORI_BASE, base);

      // Incrementa next ID
      this.saveToStorage(STORAGE_KEYS.NEXT_ID, nextId + 1);
      
      this.updateMetadata();
      
      console.log('[MOCK PERSISTENCE] Fornitore aggiunto con ID:', nextId);
      return { id: nextId, success: true };
      
    } catch (error) {
      console.error('[MOCK PERSISTENCE] Errore aggiunta fornitore:', error);
      return { id: 0, success: false };
    }
  }

  /**
   * Modifica un fornitore esistente
   */
  static updateFornitore(id: number, modifiche: Partial<FornitoreDettagliato>): boolean {
    if (!this.isStorageEnabled()) return false;

    try {
      // Aggiorna fornitori dettagliati
      const dettagliati = this.getAllFornitoriDettagliati();
      const indexDettagliato = dettagliati.findIndex(f => f.id === id);
      
      if (indexDettagliato >= 0) {
        dettagliati[indexDettagliato] = { ...dettagliati[indexDettagliato], ...modifiche };
        
        // Aggiorna anche metadati
        if (dettagliati[indexDettagliato].metadati) {
          dettagliati[indexDettagliato].metadati.data_ultima_modifica = new Date().toISOString().split('T')[0];
        }
        
        this.saveToStorage(STORAGE_KEYS.FORNITORI_DETTAGLIATI, dettagliati);
      }

      // Aggiorna fornitori base se necessario
      const base = this.getAllFornitori();
      const fornitoreBase = base.find(f => f.id === id);
      
      if (fornitoreBase && modifiche.azienda) {
        if (modifiche.azienda.ragione_sociale) {
          fornitoreBase.ragioneSociale = modifiche.azienda.ragione_sociale;
        }
        if (modifiche.azienda.email_pec) {
          fornitoreBase.email = modifiche.azienda.email_pec;
        }
        if (modifiche.azienda.telefono) {
          fornitoreBase.telefono = modifiche.azienda.telefono;
        }
        
        fornitoreBase.dataUltimaModifica = new Date().toISOString();
        
        this.saveToStorage(STORAGE_KEYS.FORNITORI_BASE, base);
      }

      this.updateMetadata();
      
      console.log('[MOCK PERSISTENCE] Fornitore aggiornato ID:', id);
      return true;
      
    } catch (error) {
      console.error('[MOCK PERSISTENCE] Errore aggiornamento fornitore:', error);
      return false;
    }
  }

  /**
   * Elimina un fornitore
   */
  static deleteFornitore(id: number): boolean {
    if (!this.isStorageEnabled()) return false;

    try {
      // Rimuovi dai fornitori base
      const base = this.getAllFornitori();
      const filteredBase = base.filter(f => f.id !== id);
      
      if (filteredBase.length === base.length) {
        return false; // Fornitore non trovato
      }
      
      this.saveToStorage(STORAGE_KEYS.FORNITORI_BASE, filteredBase);

      // Rimuovi dai fornitori dettagliati
      const dettagliati = this.getAllFornitoriDettagliati();
      const indexDettagliato = dettagliati.findIndex(f => f.id === id);
      
      if (indexDettagliato >= 0) {
        dettagliati.splice(indexDettagliato, 1);
        this.saveToStorage(STORAGE_KEYS.FORNITORI_DETTAGLIATI, dettagliati);
      }

      this.updateMetadata();
      
      console.log('[MOCK PERSISTENCE] Fornitore eliminato ID:', id);
      return true;
      
    } catch (error) {
      console.error('[MOCK PERSISTENCE] Errore eliminazione fornitore:', error);
      return false;
    }
  }

  /**
   * Ottiene i dati lookup
   */
  static getLookupData(): LookupData {
    return this.loadFromStorage(STORAGE_KEYS.LOOKUP_DATA, LOOKUP_DATA);
  }

  /**
   * Ottiene i menu items
   */
  static getMenuItems(): MenuItem[] {
    return this.loadFromStorage(STORAGE_KEYS.MENU_ITEMS, MENU_ITEMS);
  }

  /**
   * Resetta il storage ai dati di default
   */
  static resetToDefault(): void {
    if (!this.isStorageEnabled()) return;
    
    console.log('[MOCK PERSISTENCE] Reset storage ai dati di default');
    this.saveDefaultData();
  }

  /**
   * Cancella tutto il storage
   */
  static clearAll(): void {
    if (!this.isStorageEnabled()) return;
    
    Object.values(STORAGE_KEYS).forEach(key => {
      localStorage.removeItem(key);
    });
    
    console.log('[MOCK PERSISTENCE] Storage cancellato');
  }

  /**
   * Esporta tutti i dati come JSON
   */
  static exportData(): string {
    const data = {
      fornitori_base: this.getAllFornitori(),
      fornitori_dettagliati: this.getAllFornitoriDettagliati(),
      lookup_data: this.getLookupData(),
      menu_items: this.getMenuItems(),
      metadata: this.loadFromStorage(STORAGE_KEYS.METADATA, null)
    };
    
    return JSON.stringify(data, null, 2);
  }

  /**
   * Importa dati da JSON
   */
  static importData(jsonData: string): boolean {
    if (!this.isStorageEnabled()) return false;
    
    try {
      const data = JSON.parse(jsonData);
      
      if (data.fornitori_base) {
        this.saveToStorage(STORAGE_KEYS.FORNITORI_BASE, data.fornitori_base);
      }
      if (data.fornitori_dettagliati) {
        this.saveToStorage(STORAGE_KEYS.FORNITORI_DETTAGLIATI, data.fornitori_dettagliati);
      }
      if (data.lookup_data) {
        this.saveToStorage(STORAGE_KEYS.LOOKUP_DATA, data.lookup_data);
      }
      if (data.menu_items) {
        this.saveToStorage(STORAGE_KEYS.MENU_ITEMS, data.menu_items);
      }
      
      this.updateMetadata();
      
      console.log('[MOCK PERSISTENCE] Dati importati con successo');
      return true;
      
    } catch (error) {
      console.error('[MOCK PERSISTENCE] Errore importazione dati:', error);
      return false;
    }
  }

  /**
   * Ottiene statistiche del storage
   */
  static getStats(): any {
    if (!this.isStorageEnabled()) {
      return { enabled: false };
    }

    const metadata = this.loadFromStorage<StorageMetadata | null>(STORAGE_KEYS.METADATA, null);
    const fornitori = this.getAllFornitori();
    
    return {
      enabled: true,
      totalFornitori: fornitori.length,
      lastUpdate: metadata?.lastUpdate,
      version: metadata?.version,
      storageUsed: this.calculateStorageSize(),
      fornitori_per_stato: this.groupFornitoriByStato(fornitori),
      fornitori_per_provincia: this.groupFornitoriByProvincia(fornitori)
    };
  }

  /**
   * Calcola la dimensione del storage utilizzata
   */
  private static calculateStorageSize(): string {
    let totalSize = 0;
    
    Object.values(STORAGE_KEYS).forEach(key => {
      const item = localStorage.getItem(key);
      if (item) {
        totalSize += new Blob([item]).size;
      }
    });
    
    return `${(totalSize / 1024).toFixed(2)} KB`;
  }

  /**
   * Raggruppa fornitori per stato
   */
  private static groupFornitoriByStato(fornitori: FornitoreBase[]): Record<string, number> {
    return fornitori.reduce((acc, f) => {
      acc[f.stato] = (acc[f.stato] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);
  }

  /**
   * Raggruppa fornitori per provincia
   */
  private static groupFornitoriByProvincia(fornitori: FornitoreBase[]): Record<string, number> {
    return fornitori.reduce((acc, f) => {
      acc[f.provincia] = (acc[f.provincia] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);
  }
}

// Alias per compatibilità
export const PersistenceManager = MockPersistence;

// Inizializza automaticamente il storage
MockPersistence.initializeStorage();